import React, { useState, useEffect } from 'react';
import SuperAdminLayout from '../../../components/hostel/superadmin/SuperAdminLayout';

const API_URL = process.env.REACT_APP_API_URL || 'https://app.learninghub.ind.in';

const ManageHostel = ({ onNavigate, userInfo }) => {
  const [loading, setLoading] = useState(true);
  const [hostels, setHostels] = useState([]);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedHostel, setSelectedHostel] = useState(null);
  const [formData, setFormData] = useState({
    hostel_name: '',
    hostel_type: 'boys',
    total_rooms: '',
    available_rooms: '',
    hostel_address: '',
    hostel_contact: '',
    warden_name: '',
    warden_phone: ''
  });

  useEffect(() => {
    if (userInfo?.userId) {
      fetchHostels();
    }
  }, [userInfo]);

  const fetchHostels = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${API_URL}/superadmin/hostels/${userInfo.userId}`);
      const data = await response.json();

      if (data.success) {
        setHostels(data.hostels || []);
      } else {
        setError(data.message || 'Failed to load hostels');
      }
    } catch (err) {
      console.error('Error fetching hostels:', err);
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddHostel = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_URL}/superadmin/hostel/${userInfo.userId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (data.success) {
        alert('Hostel added successfully!');
        setShowAddModal(false);
        resetForm();
        fetchHostels();
      } else {
        alert(data.message || 'Failed to add hostel');
      }
    } catch (err) {
      console.error('Error adding hostel:', err);
      alert('Failed to add hostel');
    }
  };

  const handleEditHostel = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(
        `${API_URL}/superadmin/hostel/${userInfo.userId}/${selectedHostel.hostel_id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formData)
        }
      );

      const data = await response.json();

      if (data.success) {
        alert('Hostel updated successfully!');
        setShowEditModal(false);
        setSelectedHostel(null);
        resetForm();
        fetchHostels();
      } else {
        alert(data.message || 'Failed to update hostel');
      }
    } catch (err) {
      console.error('Error updating hostel:', err);
      alert('Failed to update hostel');
    }
  };

  const handleDeleteHostel = async (hostelId) => {
    if (!window.confirm('Are you sure you want to deactivate this hostel?')) {
      return;
    }

    try {
      const response = await fetch(
        `${API_URL}/superadmin/hostel/${userInfo.userId}/${hostelId}`,
        {
          method: 'DELETE'
        }
      );

      const data = await response.json();

      if (data.success) {
        alert('Hostel deactivated successfully!');
        fetchHostels();
      } else {
        alert(data.message || 'Failed to deactivate hostel');
      }
    } catch (err) {
      console.error('Error deactivating hostel:', err);
      alert('Failed to deactivate hostel');
    }
  };

  const openEditModal = (hostel) => {
    setSelectedHostel(hostel);
    setFormData({
      hostel_name: hostel.hostel_name,
      hostel_type: hostel.hostel_type,
      total_rooms: hostel.total_rooms,
      available_rooms: hostel.available_rooms,
      hostel_address: hostel.hostel_address || '',
      hostel_contact: hostel.hostel_contact || '',
      warden_name: hostel.warden_name || '',
      warden_phone: hostel.warden_phone || ''
    });
    setShowEditModal(true);
  };

  const resetForm = () => {
    setFormData({
      hostel_name: '',
      hostel_type: 'boys',
      total_rooms: '',
      available_rooms: '',
      hostel_address: '',
      hostel_contact: '',
      warden_name: '',
      warden_phone: ''
    });
  };

  const filteredHostels = hostels.filter(hostel => {
    const matchesSearch = hostel.hostel_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      hostel.warden_name?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = filterType === 'all' || hostel.hostel_type === filterType;
    return matchesSearch && matchesType;
  });

  return (
    <SuperAdminLayout
      onNavigate={onNavigate}
      userInfo={userInfo}
      activePage="hostel-superadmin-manage-hostel"
      pageTitle="Manage Hostels 🏢"
    >
      <style>{`
        .controls-section {
          display: flex;
          gap: 20px;
          margin-bottom: 30px;
          flex-wrap: wrap;
          align-items: center;
        }

        .search-box {
          flex: 1;
          min-width: 300px;
          position: relative;
        }

        .search-input {
          width: 100%;
          padding: 14px 20px 14px 50px;
          border: 2px solid rgba(82,171,152,0.2);
          border-radius: 14px;
          font-size: 15px;
          font-family: 'Inter', sans-serif;
          transition: all 0.3s;
          background: white;
        }

        .search-input:focus {
          outline: none;
          border-color: #52ab98;
          box-shadow: 0 0 0 4px rgba(82,171,152,0.1);
        }

        .search-icon {
          position: absolute;
          left: 18px;
          top: 50%;
          transform: translateY(-50%);
          font-size: 20px;
          color: #52ab98;
        }

        .filter-select {
          padding: 14px 20px;
          border: 2px solid rgba(82,171,152,0.2);
          border-radius: 14px;
          font-size: 15px;
          font-family: 'Inter', sans-serif;
          font-weight: 600;
          color: #2b6777;
          background: white;
          cursor: pointer;
          transition: all 0.3s;
        }

        .filter-select:focus {
          outline: none;
          border-color: #52ab98;
          box-shadow: 0 0 0 4px rgba(82,171,152,0.1);
        }

        .add-btn {
          padding: 14px 28px;
          background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
          color: white;
          border: none;
          border-radius: 14px;
          font-size: 15px;
          font-weight: 700;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 10px;
          transition: all 0.3s;
          box-shadow: 0 4px 20px rgba(43,103,119,0.3);
        }

        .add-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 30px rgba(43,103,119,0.4);
        }

        .hostels-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
          gap: 24px;
        }

        .hostel-card {
          background: white;
          border-radius: 20px;
          padding: 28px;
          border: 2px solid rgba(82,171,152,0.15);
          transition: all 0.3s;
          position: relative;
          overflow: hidden;
        }

        .hostel-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 6px;
          height: 100%;
          background: linear-gradient(180deg, #2b6777 0%, #52ab98 100%);
        }

        .hostel-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 40px rgba(43,103,119,0.15);
          border-color: rgba(82,171,152,0.3);
        }

        .hostel-header {
          display: flex;
          align-items: flex-start;
          gap: 16px;
          margin-bottom: 20px;
        }

        .hostel-icon {
          width: 60px;
          height: 60px;
          background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
          border-radius: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 28px;
          color: white;
          font-weight: 800;
          flex-shrink: 0;
        }

        .hostel-info {
          flex: 1;
        }

        .hostel-name {
          font-size: 20px;
          font-weight: 800;
          color: #2b6777;
          margin-bottom: 6px;
        }

        .hostel-type-badge {
          display: inline-block;
          padding: 6px 14px;
          background: rgba(82,171,152,0.15);
          color: #2b6777;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 700;
          text-transform: capitalize;
        }

        .hostel-details {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 16px;
          margin-bottom: 20px;
        }

        .detail-item {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .detail-label {
          font-size: 12px;
          color: rgba(43,103,119,0.6);
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .detail-value {
          font-size: 16px;
          font-weight: 700;
          color: #2b6777;
        }

        .hostel-actions {
          display: flex;
          gap: 10px;
          padding-top: 20px;
          border-top: 2px solid rgba(82,171,152,0.1);
        }

        .action-btn-edit,
        .action-btn-delete {
          flex: 1;
          padding: 12px;
          border: none;
          border-radius: 12px;
          font-size: 14px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.3s;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }

        .action-btn-edit {
          background: rgba(82,171,152,0.15);
          color: #2b6777;
        }

        .action-btn-edit:hover {
          background: rgba(82,171,152,0.25);
          transform: translateY(-2px);
        }

        .action-btn-delete {
          background: rgba(239,68,68,0.15);
          color: #dc2626;
        }

        .action-btn-delete:hover {
          background: rgba(239,68,68,0.25);
          transform: translateY(-2px);
        }

        .modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(43,103,119,0.6);
          backdrop-filter: blur(8px);
          z-index: 1000;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
        }

        .modal {
          background: white;
          border-radius: 24px;
          width: 100%;
          max-width: 600px;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: 0 24px 70px rgba(43,103,119,0.3);
        }

        .modal-header {
          padding: 28px 32px;
          border-bottom: 2px solid rgba(82,171,152,0.15);
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .modal-title {
          font-size: 24px;
          font-weight: 900;
          color: #2b6777;
        }

        .modal-close {
          background: rgba(82,171,152,0.15);
          border: none;
          width: 40px;
          height: 40px;
          border-radius: 12px;
          cursor: pointer;
          font-size: 20px;
          color: #2b6777;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s;
        }

        .modal-close:hover {
          background: rgba(82,171,152,0.25);
          transform: rotate(90deg);
        }

        .modal-body {
          padding: 32px;
        }

        .form-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
        }

        .form-group {
          margin-bottom: 24px;
        }
        
        .form-group.full-width {
            grid-column: span 2;
        }

        .form-label {
          display: block;
          font-size: 14px;
          font-weight: 700;
          color: #2b6777;
          margin-bottom: 8px;
        }

        .form-input {
          width: 100%;
          padding: 14px 18px;
          border: 2px solid rgba(82,171,152,0.2);
          border-radius: 12px;
          font-size: 15px;
          font-family: 'Inter', sans-serif;
          transition: all 0.3s;
        }

        .form-input:focus {
          outline: none;
          border-color: #52ab98;
          box-shadow: 0 0 0 4px rgba(82,171,152,0.1);
        }

        .modal-footer {
          padding: 24px 32px;
          border-top: 2px solid rgba(82,171,152,0.15);
          display: flex;
          gap: 12px;
          justify-content: flex-end;
        }

        .btn-cancel,
        .btn-submit {
          padding: 14px 28px;
          border: none;
          border-radius: 12px;
          font-size: 15px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.3s;
        }

        .btn-cancel {
          background: rgba(107,114,128,0.15);
          color: #374151;
        }

        .btn-cancel:hover {
          background: rgba(107,114,128,0.25);
        }

        .btn-submit {
          background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
          color: white;
          box-shadow: 0 4px 20px rgba(43,103,119,0.3);
        }

        .btn-submit:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 30px rgba(43,103,119,0.4);
        }

        .empty-state {
          text-align: center;
          padding: 80px 20px;
          color: #666;
        }

        .empty-state-icon {
          font-size: 80px;
          margin-bottom: 20px;
        }

        .empty-state-text {
          font-size: 18px;
          color: #2b6777;
          font-weight: 600;
        }

        @media (max-width: 768px) {
          .controls-section {
            flex-direction: column;
            gap: 16px;
          }
          
          .search-box {
            width: 100%;
          }
          
          .add-btn {
            width: 100%;
            justify-content: center;
          }
          
          .form-grid {
              grid-template-columns: 1fr;
          }
          
          .form-group.full-width {
              grid-column: span 1;
          }
        }
      `}</style>

      {/* Controls */}
      <div className="controls-section">
        <div className="search-box">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            placeholder="Search hostilities by name or warden..."
            className="search-input"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <select
          className="filter-select"
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
        >
          <option value="all">All Types</option>
          <option value="boys">Boys Hostel</option>
          <option value="girls">Girls Hostel</option>
        </select>

        <button className="add-btn" onClick={() => setShowAddModal(true)}>
          ➕ Add New Hostel
        </button>
      </div>

      {/* Hostels Grid */}
      {loading ? (
        <div className="empty-state">
          <div className="empty-state-icon">⏳</div>
          <div className="empty-state-text">Loading hostels...</div>
        </div>
      ) : error ? (
        <div className="empty-state">
          <div className="empty-state-icon">❌</div>
          <div className="empty-state-text">{error}</div>
        </div>
      ) : filteredHostels.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">🏢</div>
          <div className="empty-state-text">No hostels found</div>
        </div>
      ) : (
        <div className="hostels-grid">
          {filteredHostels.map((hostel) => (
            <div key={hostel.hostel_id} className="hostel-card">
              <div className="hostel-header">
                <div className="hostel-icon">🏢</div>
                <div className="hostel-info">
                  <div className="hostel-name">{hostel.hostel_name}</div>
                  <span className="hostel-type-badge">{hostel.hostel_type} Hostel</span>
                </div>
              </div>

              <div className="hostel-details">
                <div className="detail-item">
                  <span className="detail-label">Total Rooms</span>
                  <span className="detail-value">{hostel.total_rooms}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Available</span>
                  <span className="detail-value">{hostel.available_rooms}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Warden</span>
                  <span className="detail-value">{hostel.warden_name || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Contact</span>
                  <span className="detail-value">{hostel.warden_phone || 'N/A'}</span>
                </div>
              </div>

              <div className="hostel-actions">
                <button
                  className="action-btn-edit"
                  onClick={() => openEditModal(hostel)}
                >
                  ✏️ Edit
                </button>
                <button
                  className="action-btn-delete"
                  onClick={() => handleDeleteHostel(hostel.hostel_id)}
                >
                  🗑️ Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      {(showAddModal || showEditModal) && (
        <div className="modal-overlay" onClick={() => {
          setShowAddModal(false);
          setShowEditModal(false);
          resetForm();
        }}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">
                {showAddModal ? '➕ Add New Hostel' : '✏️ Edit Hostel'}
              </h2>
              <button
                className="modal-close"
                onClick={() => {
                  setShowAddModal(false);
                  setShowEditModal(false);
                  resetForm();
                }}
              >
                ✕
              </button>
            </div>

            <form onSubmit={showAddModal ? handleAddHostel : handleEditHostel}>
              <div className="modal-body">
                <div className="form-grid">
                  <div className="form-group full-width">
                    <label className="form-label">Hostel Name *</label>
                    <input
                      type="text"
                      name="hostel_name"
                      className="form-input"
                      value={formData.hostel_name}
                      onChange={handleInputChange}
                      required
                      placeholder="e.g. Block A"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Hostel Type *</label>
                    <select
                      name="hostel_type"
                      className="form-input"
                      value={formData.hostel_type}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="boys">Boys Hostel</option>
                      <option value="girls">Girls Hostel</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Total Rooms *</label>
                    <input
                      type="number"
                      name="total_rooms"
                      className="form-input"
                      value={formData.total_rooms}
                      onChange={handleInputChange}
                      required
                      placeholder="e.g. 100"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Available Rooms *</label>
                    <input
                      type="number"
                      name="available_rooms"
                      className="form-input"
                      value={formData.available_rooms}
                      onChange={handleInputChange}
                      required
                      placeholder="e.g. 50"
                    />
                  </div>

                  <div className="form-group full-width">
                    <label className="form-label">Address</label>
                    <input
                      type="text"
                      name="hostel_address"
                      className="form-input"
                      value={formData.hostel_address}
                      onChange={handleInputChange}
                      placeholder="Location details"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Warden Name</label>
                    <input
                      type="text"
                      name="warden_name"
                      className="form-input"
                      value={formData.warden_name}
                      onChange={handleInputChange}
                      placeholder="Warden's full name"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Warden Contact</label>
                    <input
                      type="text"
                      name="warden_phone"
                      className="form-input"
                      value={formData.warden_phone}
                      onChange={handleInputChange}
                      placeholder="Contact number"
                    />
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => {
                    setShowAddModal(false);
                    setShowEditModal(false);
                    resetForm();
                  }}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-submit">
                  {showAddModal ? 'Save Hostel' : 'Update Hostel'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </SuperAdminLayout>
  );
};

export default ManageHostel;